import { Injectable } from '@angular/core';
import {HttpClient, HttpContext, HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import {Observable, combineLatest, of, from, throwError} from 'rxjs';
import { take, switchMap, tap, map, catchError } from 'rxjs/operators';
import { apiUrl } from '../../../../environments/environment';
import { AuthService } from '../../../shared/auth/auth.service';
import {InferenceParams, StatoSessioneService} from '../../../shared/services/stato-sessione.service';
import {
  Ambiguity,
  AnalysisContent, DataParametersResponse,
  StoryDescriptionResponse,
  SupportiveAnalysisContent,
  TokenCount,
} from '../../../shared/templateGen/templateInterfaces';
import {TestWithMetadata} from '../test/test-id.service';
import { DynamicRefinedTestCase } from '../../../shared/templateGen/templateInterfaces';
import {MacroScenarioItem, MacroScenarioMicroSpecState} from '../template/three-column-workspace/three-column-workspace.component';
import {Constraint} from '../../../shared/templateGen/template-api.service';
import {BYPASS_AUTH} from '../../../shared/auth/http-context-tokens';

export type CheckpointStage = 'scenarios' | 'acs' | 'tests';

// Aggiorna l'interfaccia SessionMetadata
export interface SessionMetadata {
  sessionId: string;
  sessionName: string;
  displayName: string;
  createdBy: string;
  createdAt: string;
  lastModified: string;
  checkpointStage: CheckpointStage;
  tokenUsage?: TokenCount;
  isShared?: boolean; // Aggiunto
  shareId?: string;   // Aggiunto
  shareExpiresAt?: string;
}

export interface SavedSessionData {
  version: string;
  timestamp: string;
  metadata: {
    projectId: string | null;
    author: string;
    sessionName: string;
    displayName: string;
    selectedModel: string;

    // i parametri di inferenza
    inferenceParams?: {
      temperature: number;
      top_p: number;
      thinking_budget?: number;
      thinking_level?: 'low' | 'high';
    };

    agenticMode: boolean;
    projectLanguage: string;
    checkpointStage: CheckpointStage;
    sessionId: string | null;
    tokenUsage?: TokenCount;
    templateName?: string; // Per 'tests'
    totalTests?: number; // Per 'tests'
  };
  context?: string; // refinedStoryString
  macroScenarios?: MacroScenarioItem[];
  microScenarioSpecifications?: { [key: string]: MacroScenarioMicroSpecState }; // Map serializzata
  detailedBreakdowns?: { [key: string]: string }; // Map serializzata
  dataParameters?: any; // DataParametersResponse
  storyDescription?: any; // StoryDescriptionResponse
  coreAnalysis?: AnalysisContent | null;
  requirementsAnalysis?: AnalysisContent | null;
  supportiveAnalysis?: SupportiveAnalysisContent | null;
  ambiguities?: any[]; // Ambiguity[]
  constraints?: Constraint[];
  storyHistory?: any; // StoryHistory — cronologia turni e storia concordata
  executionPlan?: any; // ExecutionPlan dall'agente
  agentStepMessages?: Array<{ step: string; status: string; time?: string }>; // Traccia step completati
  proposedResolutions?: any[]; // Risoluzioni proposte dall'agente
  clarificationTimeline?: any; // {answeredCards, originalStory, finalStory} per timeline restore
  generatedTests?: any[]; // TestWithMetadata[], per Tests
  refinedTestCases?: { [key: string]: DynamicRefinedTestCase[] }; // Remains an object for serialization
  globalConsolidatedTests?: DynamicRefinedTestCase[]; // Nuovi test globali
  selectedTemplate?: string; // Per Tests
  selectedTemplateColumns?: string[]; // Per Tests
  istruzioniScriviTest?: string; // Per Tests
  // Potrebbe essere utile aggiungere altri campi da StatoSessioneService se necessario
  // E.g. supportFiles, retrievedNotes, etc.
}

export interface SaveSessionRequest {
  email: string;
  projectId: string;
  sessionName: string;
  displayName?: string;
  sessionData: any; // This will be your SavedAppState
  checkpointStage: CheckpointStage;
  sessionIdToUpdate?: string; // Optional: If set, update this session
  tokenUsage?: TokenCount; // Add token usage to save request
}

export interface LoadedSessionResponse {
  sessionId: string;
  sessionName: string;
  sessionData: SavedSessionData;
  checkpointStage: CheckpointStage;
  createdAt: string; // Aggiunto se presente nella risposta
  lastModified: string; // Aggiunto se presente nella risposta
  tokenUsage?: TokenCount; // Aggiunto se presente a questo livello
}

@Injectable({
  providedIn: 'root'
})
export class SessionPersistenceService {
  private apiUrl = apiUrl;
  private currentSessionId: string | null = null;
  private currentSessionName: string | null = null;
  private isNewWorkflow = true;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private sessioneService: StatoSessioneService
  ) {}

  /**
   * Gets authentication headers with token
   * @returns Observable of HttpHeaders with the auth token
   */
  private getHeaders(): Observable<HttpHeaders> {
    return this.authService.token$.pipe(
      take(1),
      map(token => {
        return new HttpHeaders().set('Authorization', `Bearer ${token}`);
      })
    );
  }

  public getCurrentSessionId(): string | null {
    return this.currentSessionId;
  }

  /**
   * Save or update the current session.
   * The session name is now ALWAYS taken from StatoSessioneService first.
   * A timestamped name is generated ONLY as a last resort if no name is set.
   * @param checkpointStage The checkpoint stage for the save.
   * @returns Observable with the response from the save operation.
   */
  saveCurrentSession(checkpointStage: CheckpointStage = 'tests'): Observable<any> {
    // 1. Get SessionName (ID) and DisplayName (Title)
    const sessionName = this.sessioneService.getSessionName();
    let displayName = this.sessioneService.getDisplayName();

    // Logic: If sessionName (ID) is missing, generate it from the filename or generic
    let resolvedSessionName = sessionName;

    if (!resolvedSessionName || resolvedSessionName.trim() === '') {
      const baseName = this.sessioneService.getFileName() || 'Untitled Session';
      // Generate unique immutable ID
      resolvedSessionName = this.generateUniqueSessionName(baseName);
      this.sessioneService.updateSessionName(resolvedSessionName); // Set it once
    }

    // Ensure display name exists — auto-generate from story text + date
    if (!displayName) {
      displayName = this.generateDisplayName();
      this.sessioneService.updateDisplayName(displayName);
    }

    return this.getHeaders().pipe(
      switchMap(headers => {
        return combineLatest([
          this.authService.email$.pipe(take(1)),
          this.sessioneService.projectId$.pipe(take(1)),
          this.sessioneService.tokenUsage$.pipe(take(1))
        ]).pipe(
          switchMap(([email, projectId, tokenUsage]) => {
            if (!email || !projectId) {
              throw new Error('Missing email or project ID');
            }

            return from(this.getAppStateForCheckpoint(email, resolvedSessionName, checkpointStage)).pipe(
              switchMap(sessionData => {
                // Update metadata in the data blob
                if (sessionData.metadata) {
                  sessionData.metadata.sessionName = resolvedSessionName;
                  sessionData.metadata.displayName = displayName;
                }

                const request: SaveSessionRequest = {
                  email,
                  projectId,
                  sessionName: resolvedSessionName, // The Immutable ID
                  displayName: displayName,         // The Mutable Title
                  sessionData,
                  checkpointStage,
                  tokenUsage
                };


                console.log('%c[DEBUG] Request Body Being Sent:', 'color: blue; font-weight: bold;', JSON.stringify(request, null, 2));

                if (this.currentSessionId && !this.isNewWorkflow) {
                  request.sessionIdToUpdate = this.currentSessionId;
                }

                return this.http.post<any>(`${this.apiUrl}/persistsession/`, request, { headers }).pipe(
                  tap((response: any) => {
                    if (response && response.sessionId) {
                      this.currentSessionId = response.sessionId;
                      this.currentSessionName = resolvedSessionName; // Use the name we saved with
                      this.isNewWorkflow = false;

                      // Ensure state service is in sync
                      this.sessioneService.updateSessionName(this.currentSessionName);
                      this.sessioneService.updateFileName(this.currentSessionName);
                    }
                  }),
                  catchError((error: HttpErrorResponse) => {
                    if (error.status === 409) {
                      return throwError(new Error(error.error.detail || 'A session with this name already exists.'));
                    }
                    console.error('Error saving session:', error);
                    return throwError(error);
                  })
                );
              })
            );
          })
        );
      })
    );
  }

  /**
   * Generate a unique session name by adding a timestamp
   */
  private generateUniqueSessionName(baseName: string): string {
    // Remove file extension if present
    const nameWithoutExtension = baseName.replace(/\.[^/.]+$/, '');

    // Add a timestamp to ensure uniqueness
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    return `${nameWithoutExtension} (${timestamp})`;
  }

  /**
   * Generate a human-readable display name from the first N characters of the story + date
   */
  private generateDisplayName(): string {
    const storyText = this.sessioneService.getUserStory() || '';
    // Strip HTML tags and get plain text
    const plainText = storyText.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    const maxChars = 50;
    let preview = plainText.substring(0, maxChars);
    if (plainText.length > maxChars) {
      preview = preview.replace(/\s+\S*$/, '') + '...'; // cut at word boundary
    }

    const now = new Date();
    const date = now.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

    if (preview) {
      return `${preview} — ${date}`;
    }
    return `Untitled Session — ${date}`;
  }

  /**
   * Reset the session tracking to allow creation of a new session
   */
  resetSessionTracking(): void {
    // // console.log('Resetting session tracking');
    this.currentSessionId = null;
    this.currentSessionName = null;
    this.isNewWorkflow = true;
  }

  /**
   * Set the current session ID and name for updating
   * Used when loading an existing session
   */
  setCurrentSession(sessionId: string, sessionName: string): void {
    // console.log(`DEBUG: setCurrentSession called with ID: ${sessionId}, Name: ${sessionName}`);
    this.currentSessionId = sessionId;
    this.currentSessionName = sessionName;
    this.isNewWorkflow = false;
    // console.log(`DEBUG: After setting - currentSessionId: ${this.currentSessionId}, isNewWorkflow: ${this.isNewWorkflow}`);
  }

  /**
   * Get all sessions for the current project.
   * @returns Observable with array of session metadata
   */
  getUserSessions(): Observable<SessionMetadata[]> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        // Usiamo solo projectId$, non più l'email
        return this.sessioneService.projectId$.pipe(
          take(1),
          switchMap(projectId => {
            if (!projectId) {
              throw new Error('Missing project ID');
            }

            // L'URL ora contiene solo projectId
            const url = `${this.apiUrl}/persistsession/list?projectId=${projectId}`;

            return this.http.get<SessionMetadata[]>(url, { headers });
          })
        );
      })
    );
  }

  /**
   * Load a specific session by ID
   * @param sessionId The ID of the session to load
   * @returns Observable with the session data
   */
  loadSession(sessionId: string): Observable<LoadedSessionResponse> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.get<LoadedSessionResponse>(`${this.apiUrl}/persistsession/${sessionId}`, { headers }).pipe(
          // **** AGGIUNGI QUESTO BLOCCO TAP PER IL DEBUG ****
          tap({
            next: (response) => {
              console.log('%c[SERVICE - loadSession] Raw HTTP Response:', 'color: blue; font-weight: bold;', response);

              if (!response) {
                console.error('%c[SERVICE - loadSession] Response is null or undefined.', 'color: red;');
              } else if (typeof response.sessionId === 'undefined') {
                console.error('%c[SERVICE - loadSession] CRITICAL: sessionId is UNDEFINED in the response object.', 'color: red; font-weight: bold;');
              } else {
                console.log(`%c[SERVICE - loadSession] SUCCESS: sessionId found in response: ${response.sessionId}`, 'color: green;');
              }
            },
            error: (err) => {
              console.error('%c[SERVICE - loadSession] HTTP call failed:', 'color: red;', err);
            }
          }),
          // Il vecchio tap con la logica di popolamento dello stato rimane.
          tap((response: LoadedSessionResponse) => {
            if (response && response.sessionData) {
              this.populateStateFromLoadedSession(response, false);
              this.isNewWorkflow = false;
            } else {
              console.error('[SERVICE - populateState] Loaded session is null or sessionData is missing:', response);
              throw new Error('Failed to load session data properly.');
            }
          }),
          catchError(err => {
            console.error('[SERVICE - loadSession] Error caught in catchError:', err);
            throw err;
          })
        )
      )
    );
  }

  public populateStateFromLoadedSession(loadedSessionResponse: LoadedSessionResponse, isSharedLoad = false): void {
    const { sessionData: data, sessionName } = loadedSessionResponse;

    this.sessioneService.resetAll();
    this.sessioneService.setReadOnly(isSharedLoad);

    if (!isSharedLoad) {
      // Per i caricamenti normali, tracciamo la sessione per futuri salvataggi
      const actualSessionId = loadedSessionResponse.sessionId || (loadedSessionResponse as any)._id;
      this.setCurrentSession(actualSessionId, sessionName);
    } else {
      // Per i caricamenti condivisi, NON tracciamo la sessione. È una copia "volatile".
      this.resetSessionTracking();
    }

    // 1. Set the Immutable Session Name
    this.sessioneService.updateSessionName(sessionName);

    // 2. Set the Mutable Display Name
    // Fallback to sessionName if displayName doesn't exist in old saves
    const loadedDisplayName = data.metadata.displayName || sessionName;
    this.sessioneService.updateDisplayName(loadedDisplayName);

    // Update filename based on display name (usually preferred by users)
    this.sessioneService.updateFileName(loadedDisplayName);

    if (data.metadata) {
      if (data.metadata.projectId) this.sessioneService.updateProjectId(data.metadata.projectId, true);

      if (data.metadata.inferenceParams) {
        // Se i parametri sono salvati, li usiamo
        this.sessioneService.updateInferenceParams(data.metadata.inferenceParams);
      } else {
        // Altrimenti, per retrocompatibilità, usiamo il vecchio campo `selectedModel`
        // e lo passiamo a updateInferenceParams
        this.sessioneService.updateInferenceParams({ model: data.metadata.selectedModel || 'Gemini 3' });
      }

      this.sessioneService.updateAgenticMode(data.metadata.agenticMode || false);
      this.sessioneService.updateProjectLanguage(data.metadata.projectLanguage || 'en');
      this.sessioneService.updateAgenticMode(data.metadata.agenticMode || false);
      this.sessioneService.updateProjectLanguage(data.metadata.projectLanguage || 'en');

      // Prendi tokenUsage dall'oggetto della risposta, non da una variabile inesistente.
      const sessionTokenUsage = loadedSessionResponse.tokenUsage || data.metadata.tokenUsage;

      // NEW LOGGING ADDED: Log the object we extracted before trying to use it.
      console.log('%c[Populate State] Extracted tokenUsage object:', 'color: blue;', sessionTokenUsage);

      if (sessionTokenUsage) {
        // NEW LOGGING ADDED: Confirming we entered the 'if' block.
        console.log('%c[Populate State] tokenUsage found. Sanitizing...', 'color: green;');

        const sanitizedTokenUsage: TokenCount = {
          prompt_tokens: sessionTokenUsage.prompt_tokens ?? 0,
          completion_tokens: sessionTokenUsage.completion_tokens ?? 0,
          total_tokens: sessionTokenUsage.total_tokens ?? 0
        };

        // NEW LOGGING ADDED: This is the most important log. It shows the final, cleaned object.
        console.log('%c[Populate State] Sanitized tokenUsage object to be set in state:', 'color: green; font-weight: bold;', sanitizedTokenUsage);

        this.sessioneService.setTokenUsage(sanitizedTokenUsage);
      } else {
        // NEW LOGGING ADDED: Confirming we entered the 'else' block.
        console.log('%c[Populate State] tokenUsage NOT found in response. Resetting state.', 'color: orange;');
        this.sessioneService.resetTokenUsage(); // Ensure resetTokenUsage() sets values to 0, not null.
      }
    }

    // Ripristina il contesto e i dati di analisi
    if (data.context) this.sessioneService.updateRefinedStoryString(data.context);
    this.sessioneService.updateAnalysisData(data.coreAnalysis || null, data.requirementsAnalysis || null, data.supportiveAnalysis || null);
    if (data.ambiguities) this.sessioneService.updateAmbiguities(data.ambiguities as Ambiguity[]);

    // Ripristina i dati dei CONSTRAINTS
    if (data.constraints && Array.isArray(data.constraints)) {
      this.sessioneService.updateExtractedConstraints(data.constraints as Constraint[]);
    } else {
      this.sessioneService.updateExtractedConstraints([]); // Pulisci i constraints se non presenti nella sessione caricata
    }

    // Ripristina i dati dello scenario generator
    if (data.macroScenarios) this.sessioneService.updateMacroScenarios(data.macroScenarios as MacroScenarioItem[]);
    if (data.microScenarioSpecifications) {
      const microSpecMap = new Map(Object.entries(data.microScenarioSpecifications));
      this.sessioneService.updateMicroScenarioSpecifications(microSpecMap);
    } else {
      this.sessioneService.updateMicroScenarioSpecifications(null);
    }
    if (data.detailedBreakdowns) {
      const detailedBreakdownsMap = new Map(Object.entries(data.detailedBreakdowns));
      this.sessioneService.updateDetailedBreakdowns(detailedBreakdownsMap);
    } else {
      this.sessioneService.updateDetailedBreakdowns(null);
    }

    // Ripristina i dati descrittivi
    if (data.storyDescription) this.sessioneService.updateStoryDescription(data.storyDescription as StoryDescriptionResponse);
    if (data.dataParameters) this.sessioneService.updateDataParameters(data.dataParameters as DataParametersResponse);

    // Ripristina i dati dei test
    this.sessioneService.updateGeneratedTests(data.generatedTests as TestWithMetadata[] || []);
    if (data.selectedTemplate) this.sessioneService.updateSelectedTemplate(data.selectedTemplate);
    if (data.istruzioniScriviTest) this.sessioneService.updateIstruzioniScriviTest(data.istruzioniScriviTest);

    // Ripristina i test consolidati per-macro (vecchio formato)
    if (data.refinedTestCases) {
      const refinedTestCasesMap = new Map(Object.entries(data.refinedTestCases));
      this.sessioneService.setAllRefinedTestCasesMap(refinedTestCasesMap);
    } else {
      this.sessioneService.setAllRefinedTestCasesMap(new Map());
    }

    // RIPRISTINA I TEST CONSOLIDATI GLOBALI (NUOVO)
    if (data.globalConsolidatedTests && Array.isArray(data.globalConsolidatedTests)) {
      this.sessioneService.updateGlobalConsolidatedTests(data.globalConsolidatedTests);
    } else {
      this.sessioneService.updateGlobalConsolidatedTests([]);
    }

    // Ripristina dati flusso agentico
    if (data.executionPlan) this.sessioneService.updateExecutionPlan(data.executionPlan);
    if (data.agentStepMessages?.length) this.sessioneService.updateAgentStepMessages(data.agentStepMessages);
    if (data.storyHistory?.length) this.sessioneService.updateStoryHistory(data.storyHistory);
    if (data.proposedResolutions?.length) this.sessioneService.updateProposedResolutions(data.proposedResolutions);
    if (data.clarificationTimeline) this.sessioneService.updateClarificationTimeline(data.clarificationTimeline);

    // Mark as saved since we just loaded from persistence
    this.sessioneService.markSaved();
  }

  /**
   * Hide a specific session by ID (soft delete)
   * @param sessionId The ID of the session to hide
   */
  hideSession(sessionId: string): Observable<any> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.put(`${this.apiUrl}/persistsession/${sessionId}/hide`, {}, { headers })
      )
    );
  }

  /**
   * Delete a specific session by ID (hard delete, admin only)
   * @param sessionId The ID of the session to delete
   */
  deleteSession(sessionId: string): Observable<any> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.delete(`${this.apiUrl}/persistsession/${sessionId}`, { headers })
      )
    );
  }

  /**
   * Updates the DISPLAY name.
   * Since sessionName is now immutable, we don't need to check for ID conflicts
   * when the user simply renames the title in the UI.
   */
  public updateDisplayName(newDisplayName: string): Observable<any> {
    const currentDisplayName = this.sessioneService.getDisplayName();

    // If unchanged, do nothing
    if (newDisplayName === currentDisplayName) {
      return of({ success: true, message: 'Name is unchanged.' });
    }

    // 1. Update State
    this.sessioneService.updateDisplayName(newDisplayName);

    // 2. Save
    // We assume the session already has an immutable sessionName (ID).
    // calling saveCurrentSession will pick up the new DisplayName from state
    // and save it into the metadata/DB without changing the sessionName.
    return this.saveCurrentSession();
  }

  /**
   * Uses the existing state aggregation logic but selects only the fields
   * that are relevant for the current checkpoint stage.
   *
   * UPDATED: Now captures displayName separately from sessionName.
   * - sessionName (argument): Acts as the Immutable ID.
   * - appData.displayName (observable): Acts as the Mutable User-Facing Title.
   */
  private async getAppStateForCheckpoint(
    author: string,
    sessionName: string, // This is the Immutable ID passed from saveCurrentSession
    checkpointStage: CheckpointStage
  ): Promise<SavedSessionData> {

    const appData = await combineLatest([
      this.sessioneService.projectId$,
      this.sessioneService.inferenceParams$,
      this.sessioneService.isAgenticMode$,
      this.sessioneService.projectLanguage$,
      this.sessioneService.tokenUsage$,
      this.sessioneService.refinedStoryString$,
      this.sessioneService.macroScenarios$,
      this.sessioneService.microScenarioSpecifications$,
      this.sessioneService.detailedBreakdowns$,
      this.sessioneService.dataParameters$,
      this.sessioneService.storyDescription$,
      this.sessioneService.coreAnalysis$,
      this.sessioneService.requirementsAnalysis$,
      this.sessioneService.supportiveAnalysis$,
      this.sessioneService.ambiguities$,
      this.sessioneService.extractedConstraints$,
      this.sessioneService.generatedTests$,
      this.sessioneService.refinedTestCases$,
      this.sessioneService.globalConsolidatedTests$,
      this.sessioneService.selectedTemplate$,
      this.sessioneService.templates$,
      this.sessioneService.istruzioniScriviTest$,
      // 1. NEW: Include the display name observable in the combineLatest array
      this.sessioneService.displayName$,
      // Agentic flow state
      this.sessioneService.executionPlan$,
      this.sessioneService.agentStepMessages$,
      this.sessioneService.storyHistory$,
      this.sessioneService.proposedResolutions$,
      this.sessioneService.clarificationTimeline$
    ]).pipe(
      take(1),
      map(([
             projectId,
             inferenceParams,
             agenticMode,
             projectLanguage,
             tokenUsage,
             refinedStory,
             macroScenarios,
             microSpecsFromState,
             detailedBreakdownsFromState,
             dataParams,
             storyDesc,
             coreAnalysis,
             requirementsAnalysis,
             supportiveAnalysis,
             ambiguities,
             constraints,
             genTests,
             refinedTestCasesMapFromState,
             globalConsolidatedTests,
             selTemplate,
             templates,
             istruzioniTest,
             // 2. NEW: Destructure the display name from the result array
             displayName,
             // Agentic flow
             executionPlan,
             agentStepMessages,
             storyHistory,
             proposedResolutions,
             clarificationTimeline
           ]: [
        string,
        InferenceParams,
        boolean,
        string,
        TokenCount,
        string,
        MacroScenarioItem[],
          Map<string, MacroScenarioMicroSpecState> | null,
          Map<string, string> | null,
          DataParametersResponse | undefined,
          StoryDescriptionResponse | undefined,
          AnalysisContent | null,
          AnalysisContent | null,
          SupportiveAnalysisContent | null,
        Ambiguity[],
        Constraint[],
        TestWithMetadata[],
          Map<string, DynamicRefinedTestCase[]> | null,
        DynamicRefinedTestCase[],
        string,
        any[],
        string,
        // 3. NEW: Define the type for display name
        string,
        // Agentic flow types
        any | null,
        Array<{ step: string; status: string; time?: string }>,
        any[],
        any[],
        any
      ]) => {
        return {
          projectId,
          inferenceParams,
          agenticMode,
          projectLanguage,
          tokenUsage,
          refinedStory,
          macroScenarios,
          microSpecs: microSpecsFromState,
          detailedBreakdowns: detailedBreakdownsFromState,
          dataParams,
          storyDesc,
          coreAnalysis,
          requirementsAnalysis,
          supportiveAnalysis,
          ambiguities,
          constraints,
          genTests,
          refinedTestCasesMap: refinedTestCasesMapFromState,
          globalConsolidatedTests,
          selTemplate,
          templates,
          istruzioniTest,
          // 4. NEW: Return the display name in the data object
          displayName,
          // Agentic flow
          executionPlan,
          agentStepMessages,
          storyHistory,
          proposedResolutions,
          clarificationTimeline
        };
      })
    ).toPromise();

    if (!appData) {
      console.error('%cPERSISTENCE: getAppStateForCheckpoint - appData is null/undefined. Cannot proceed.', "color: red;");
      throw new Error("Failed to retrieve app data from StatoSessioneService for checkpoint.");
    }

    // Convert Maps to Objects for serialization
    const microSpecsObject = appData.microSpecs ? Object.fromEntries(appData.microSpecs) : {};
    const breakdownsObject = appData.detailedBreakdowns ? Object.fromEntries(appData.detailedBreakdowns) : {};
    const refinedTestCasesObj: { [key: string]: DynamicRefinedTestCase[] } = {};
    if (appData.refinedTestCasesMap) {
      appData.refinedTestCasesMap.forEach((value, key) => {
        refinedTestCasesObj[key] = value;
      });
    }

    // Construct the state object to be saved
    const state: SavedSessionData = {
      version: '1.4.0',
      timestamp: new Date().toISOString(),
      metadata: {
        projectId: appData.projectId,
        author,
        // 5. UPDATED: sessionName is the Immutable ID
        sessionName: sessionName,
        // 6. NEW: displayName is the User Title. Fallback to ID if empty.
        displayName: appData.displayName || sessionName,
        selectedModel: appData.inferenceParams.model,
        inferenceParams: appData.inferenceParams,
        agenticMode: appData.agenticMode,
        projectLanguage: appData.projectLanguage,
        checkpointStage,
        sessionId: this.currentSessionId,
        tokenUsage: appData.tokenUsage
      },
      context: appData.refinedStory || undefined,
      macroScenarios: appData.macroScenarios || [],
      microScenarioSpecifications: microSpecsObject,
      detailedBreakdowns: breakdownsObject,
      dataParameters: appData.dataParams || undefined,
      storyDescription: appData.storyDesc || undefined,
      coreAnalysis: appData.coreAnalysis || null,
      requirementsAnalysis: appData.requirementsAnalysis || null,
      supportiveAnalysis: appData.supportiveAnalysis || null,
      ambiguities: appData.ambiguities || [],
      constraints: appData.constraints || [],
      // Agentic flow state
      storyHistory: appData.storyHistory?.length ? appData.storyHistory : undefined,
      executionPlan: appData.executionPlan || undefined,
      agentStepMessages: appData.agentStepMessages?.length ? appData.agentStepMessages : undefined,
      proposedResolutions: appData.proposedResolutions?.length ? appData.proposedResolutions : undefined,
      clarificationTimeline: appData.clarificationTimeline || undefined,
      generatedTests: undefined,
      refinedTestCases: undefined,
      globalConsolidatedTests: undefined,
      selectedTemplate: undefined,
      selectedTemplateColumns: undefined,
      istruzioniScriviTest: undefined,
    };

    // Populate data specific to 'tests' checkpoint
    if (checkpointStage === 'tests') {
      state.metadata.templateName = appData.selTemplate;
      state.metadata.totalTests = Array.isArray(appData.genTests) ? appData.genTests.length : 0;

      state.generatedTests = appData.genTests || [];
      state.refinedTestCases = refinedTestCasesObj;
      state.globalConsolidatedTests = appData.globalConsolidatedTests || [];

      state.selectedTemplate = appData.selTemplate || undefined;
      state.istruzioniScriviTest = appData.istruzioniTest || undefined;
    }

    // Clean up empty/undefined properties to keep JSON payload small
    (Object.keys(state) as Array<keyof SavedSessionData>).forEach(key => {
      const value = state[key];

      // Don't prune analysis objects if they exist but are empty (preserves nulls)
      if (key === 'coreAnalysis' || key === 'requirementsAnalysis' || key === 'supportiveAnalysis') {
        if (value === null) {
          return;
        }
      }

      if (value === undefined || (Array.isArray(value) && value.length === 0) || (typeof value === 'object' && value !== null && Object.keys(value).length === 0)) {
        delete state[key];
      }
    });

    return state;
  }

  /**
   * Saves the current session state on demand with a custom name
   * @param sessionName Optional custom name for this save, defaults to current session name or timestamp
   * @param checkpointStage Optional stage identifier, defaults to the current workflow stage
   * @returns Observable with the save response
   */
  saveSessionOnDemand(sessionName?: string, checkpointStage?: CheckpointStage): Observable<any> {
    // Determine which checkpoint stage we're in based on available data
    // or use the provided checkpoint stage
    return this.determineCurrentStage().pipe(
      take(1),
      switchMap(detectedStage => {
        const stage = checkpointStage || detectedStage || 'tests';
        // // console.log(`Saving on-demand with name: "${saveName}" at stage: ${stage}`);

        return this.saveCurrentSession(stage);
      }),
      tap(response => {
        // // console.log('On-demand save completed:', response);
      }),
      catchError(error => {
        console.error('Error during on-demand save:', error);
        throw error;
      })
    );
  }

  /**
   * Determines the current stage of the workflow based on available data
   * @returns Observable with the detected stage
   */
  private determineCurrentStage(): Observable<CheckpointStage> {
    return combineLatest([
      // Poiché criteriaGroups è stato rimosso come concetto di persistenza,
      // il checkpoint 'acs' potrebbe non essere più distinguibile o necessario.
      // Se 'acs' aveva altri significati, la logica qui deve essere adattata.
      // Per ora, assumiamo che 'acs' non sia più un checkpoint basato sui dati.
      // of(false), // Rappresentava hasCriteriaGroups
      this.sessioneService.generatedTests$.pipe(
        map(tests => !!tests && Array.isArray(tests) && tests.length > 0)
      ),
      this.sessioneService.refinedTestCases$.pipe(
        map(rTestsMap => !!rTestsMap && rTestsMap.size > 0)
      )
    ]).pipe(
      take(1),
      map(([/* hasCriteriaGroups - rimosso */ hasTests, hasRefinedTests]) => {
        if (hasRefinedTests || hasTests) {
          return 'tests';
          // } else if (hasCriteriaGroups) { // Logica per 'acs' rimossa o da rivedere
          //   return 'acs';
        } else {
          return 'scenarios'; // Se non ci sono test, si presume sia 'scenarios'
        }
      })
    );
  }

  /**
   * Check if there's an active session being tracked
   * @returns boolean indicating if a session is currently active
   */
  public hasActiveSession(): boolean {
    // Check for both null and undefined, and ensure we have a valid session ID
    return (this.currentSessionId != null && this.currentSessionId.trim() !== '') && !this.isNewWorkflow;
  }

  /**
   * Checks with the backend if a session name is already taken for the current project.
   * @param name The session name to check.
   * @returns Observable that completes on success (200 OK) or errors on conflict (409).
   */
  private checkSessionName(name: string): Observable<any> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        return this.sessioneService.projectId$.pipe(
          take(1),
          switchMap(projectId => {
            if (!projectId) {
              return throwError(new Error('Project ID is missing.'));
            }
            const url = `${this.apiUrl}/persistsession/check-name?projectId=${projectId}&sessionName=${encodeURIComponent(name)}`;
            return this.http.get(url, { headers });
          })
        );
      })
    );
  }

  /**
   * Updates the session name. It now ALWAYS checks for name conflicts and
   * performs a full save operation for both new and existing sessions.
   * @param newName The new name for the session.
   */
  public updateSessionName(newName: string): Observable<any> {
    // If the name hasn't changed, do nothing and return success.
    if (newName === this.currentSessionName && this.hasActiveSession()) {
      return of({ success: true, message: 'Name is unchanged.' });
    }

    // --- NEW LOGIC ---

    // For an EXISTING session, we perform a full update.
    if (this.hasActiveSession()) {
      return this.executeSaveForUpdate(newName);
    }
    // For a NEW session, we first check the name, then perform the initial save.
    else {
      return this.checkSessionName(newName).pipe(
        // If checkSessionName succeeds, the name is available.
        switchMap(() => {
          // Step 1: Update the name in the central state service.
          // This is crucial so that saveCurrentSession() picks up the correct name.
          this.sessioneService.updateSessionName(newName);

          // Step 2: Trigger the full save operation.
          // This will create the new session on the backend with all the current workspace data.
          return this.saveCurrentSession();
        }),
        // The error from either checkSessionName or saveCurrentSession will be caught here
        // and propagated to the component, which will show the toast.
        catchError((error: HttpErrorResponse) => {
          if (error.status === 409) {
            return throwError(new Error(error.error.detail || 'A session with this name already exists.'));
          }
          console.error('Error during initial save from updateSessionName:', error);
          return throwError(error);
        })
      );
    }
  }

  /**
   * Helper function containing the original logic to perform a full save/update.
   * To be called by updateSessionName when a session is already active.
   */
  private executeSaveForUpdate(newName: string): Observable<any> {
    return this.getHeaders().pipe(
      switchMap(headers => combineLatest([
        this.authService.email$.pipe(take(1)),
        this.sessioneService.projectId$.pipe(take(1)),
        this.sessioneService.tokenUsage$.pipe(take(1))
      ]).pipe(
        switchMap(([email, projectId, tokenUsage]) => {
          if (!email || !projectId) {
            return throwError(new Error('Missing email or project ID'));
          }
          return this.determineCurrentStage().pipe(
            take(1),
            switchMap(currentStage => from(this.getAppStateForCheckpoint(email, newName, currentStage || 'tests')).pipe(
              switchMap(sessionData => {
                const request: SaveSessionRequest = {
                  email,
                  projectId,
                  sessionName: newName,
                  sessionData,
                  checkpointStage: currentStage || 'tests',
                  tokenUsage,
                  sessionIdToUpdate: this.currentSessionId
                };
                return this.http.post<any>(`${this.apiUrl}/persistsession/`, request, { headers }).pipe(
                  tap(response => {
                    if (response && response.sessionId) {
                      this.currentSessionId = response.sessionId;
                      this.currentSessionName = newName;
                      this.sessioneService.updateSessionName(newName);
                      this.sessioneService.updateFileName(newName);
                    }
                  }),
                  catchError((error: HttpErrorResponse) => {
                    if (error.status === 409) {
                      return throwError(new Error(error.error.detail || 'A session with this name already exists.'));
                    }
                    console.error('Error updating session name:', error);
                    return throwError(error);
                  })
                );
              })
            ))
          );
        })
      ))
    );
  }

  /**
   * Enables sharing for a session and returns the shareable ID.
   * @param sessionId The ID of the session to share.
   */
  enableSharing(sessionId: string): Observable<{ shareId: string }> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.put<{ shareId: string }>(`${this.apiUrl}/persistsession/${sessionId}/share`, {}, { headers })
      )
    );
  }

  /**
   * Disables sharing for a session.
   * @param sessionId The ID of the session to unshare.
   */
  disableSharing(sessionId: string): Observable<any> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.delete(`${this.apiUrl}/persistsession/${sessionId}/share`, { headers })
      )
    );
  }

  /**
   * Gets a shared session's data using its public shareId.
   * This method does NOT use authentication headers.
   * @param shareId The public share ID of the session.
   */
  getSharedSession(shareId: string): Observable<LoadedSessionResponse> {
    return this.http.get<LoadedSessionResponse>(`${this.apiUrl}/persistsession/shared/${shareId}`, {
      context: new HttpContext().set(BYPASS_AUTH, true)
    });
  }
}
